import type { FC } from 'react'
import { useMemo } from 'react'

import {
  filter,
  flow,
  getPropertyValue,
  isNotEmpty,
  isNotNull,
  isNotUndefined,
  isNull,
  isSame,
  map,
  unique,
} from '@hcr/utils'

import { useHoliday } from '../../../contexts'
import {
  useAccommodationDetailsQuery,
  useActivitiesQuery,
  useDestinationDetailsQuery,
  useIdToken,
  useSingleTicketsQuery,
} from '../../../hooks'
import { WarningMissingData } from '../../common'

import { createTicketPreview, LayoutTickets } from './common'
import { TicketsEmpty } from './TicketsEmpty'
import { TicketsLoading } from './TicketsLoading'
import { prepareTicketPreviewsData } from './utils'

const ticketPreview = createTicketPreview()

export const Tickets: FC = () => {
  const idToken = useIdToken()
  const holiday = useHoliday()

  const packageTickets = useAccommodationDetailsQuery(
    { idToken: String(idToken), accommodationId: Number(holiday.data.accommodationId) },
    {
      select: getPropertyValue('package_tickets'),
      enabled: isNotNull(idToken) && isNotNull(holiday.data.accommodationId),
    }
  )

  // Note: The Consumer API resort_id is represented by hotelId and mokkiId in the Optimizely API. Depending on the type of resort, these values may be the same, different or null.
  const resortsIds = useDestinationDetailsQuery(
    { destinationId: String(holiday.data.destinationId) },
    {
      select: flow(
        destinationDetails => [destinationDetails.hotelId, destinationDetails.mokkiId],
        filter(isNotNull),
        unique(isSame),
        map(Number)
      ),
      enabled: isNotNull(holiday.data.destinationId),
    }
  )

  const activities = useActivitiesQuery(
    { idToken: String(idToken), resortsIds: resortsIds.data ?? [] },
    { enabled: isNotNull(idToken) && isNotUndefined(resortsIds.data) && isNotEmpty(resortsIds.data) }
  )

  const singleTickets = useSingleTicketsQuery(
    { idToken: String(idToken), resortsIds: resortsIds.data ?? [] },
    { enabled: isNotNull(idToken) && isNotUndefined(resortsIds.data) && isNotEmpty(resortsIds.data) }
  )

  const ticketPreviewsData = useMemo(
    () => prepareTicketPreviewsData([packageTickets.data, activities.data, singleTickets.data].filter(isNotUndefined)),
    [packageTickets.data, activities.data, singleTickets.data]
  )

  if (holiday.isSuccess && isNull(holiday.data.accommodationId) && isNull(holiday.data.destinationId)) {
    return <TicketsEmpty />
  }

  if (packageTickets.isSuccess || activities.isSuccess || singleTickets.isSuccess) {
    if (isNotEmpty(ticketPreviewsData)) {
      return (
        <LayoutTickets>
          {(packageTickets.isError || resortsIds.isError || activities.isError || singleTickets.isError) && (
            <WarningMissingData />
          )}
          {ticketPreviewsData.map(ticketPreview)}
        </LayoutTickets>
      )
    }

    return <TicketsEmpty />
  }

  return <TicketsLoading />
}
